import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApplicationTrackerContext } from '../../contexts/ApplicationTrackerContext';
import { GlobalContext } from '../../contexts/GlobalContext';
import axiosInstance from '../../utils/axiosConfig';
import TutorialTooltip from '../DailyDigest/components/TutorialTooltip';
import TutorialRestartButton from '../TutorialRestartButton';
import { JOB_BOARD_TUTORIAL_STEPS } from './config/tutorialConfig';
import JobComponent from './JobComponent';
import JobFilterModal from './JobFilterModal';
import ExternalLink from '../common/ExternalLink';

function JobBoard() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { userProfile, updateTutorialFlag } = useContext(GlobalContext);
  const { currentApps } = useContext(ApplicationTrackerContext);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    page_size: 10,
    total_pages: 0,
  });
  const [filters, setFilters] = useState({
    recent: false,
    workModel: 'all',
  });
  const [showAppliedJobs, setShowAppliedJobs] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Check if user is from Paul Allen School (UW CS)
  const isFromPaulAllenSchool = userProfile?.email?.endsWith('@cs.washington.edu');
  const isFromUW = userProfile?.email?.endsWith('@uw.edu');

  // Tutorial state
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    if (userProfile) {
      setShowTutorial(userProfile.onboarding_progress.hasSeenJobBoardTutorial !== true);
    }
  }, [userProfile]);
  const [currentTutorialStep, setCurrentTutorialStep] = useState(0);

  // Refs for tutorial elements
  const nodeRefs = {
    'job-listings': useRef(null),
    'add-application': useRef(null),
    'apply-now': useRef(null),
  };

  // Add new state for element readiness
  const [elementsReady, setElementsReady] = useState(false);

  // Add state to track the previous step's data
  const [previousStepData, setPreviousStepData] = useState(null);

  // Add new state for filter modal
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    jobFocus: '',
    visaStatus: '',
    minEducationLevel: '',
    workLocation: '',
    internshipSeason: '',
    housingStatus: '',
    minGpa: '',
    minGradYear: '',
    maxGradYear: '',
    requiresSecurityClearance: false,
    companySize: '',
  });

  // Update the effect that checks for element readiness
  useEffect(() => {
    if (showTutorial) {
      const checkElements = () => {
        const currentStep = JOB_BOARD_TUTORIAL_STEPS[currentTutorialStep];
        const currentRef = nodeRefs[currentStep.id];

        // First step check
        if (currentStep.id === 'job-listings') {
          if (currentRef?.current) {
            setElementsReady(true);
          } else {
            setTimeout(checkElements, 10);
          }
          return;
        }

        // For add-application and apply-now buttons
        // Wait for roles to be loaded and DOM to be updated
        if (roles.length > 0) {
          // Give a small delay to ensure DOM is updated
          setTimeout(() => {
            if (currentRef?.current) {
              setElementsReady(true);
            } else {
              // If still not found, try again
              setTimeout(checkElements, 10);
            }
          }, 50);
        } else {
          setTimeout(checkElements, 10);
        }
      };

      // // Reset elementsReady when step changes
      // setElementsReady(false);
      // Start checking for elements
      checkElements();
    }
  }, [showTutorial, currentTutorialStep, roles]); // Keep these dependencies

  // Add a debug effect to monitor state changes
  useEffect(() => {
    if (showTutorial) {
      console.log('Tutorial state:', {
        currentStep: JOB_BOARD_TUTORIAL_STEPS[currentTutorialStep].id,
        elementsReady,
        rolesLoaded: roles.length > 0,
      });
    }
  }, [showTutorial, currentTutorialStep, elementsReady, roles]);

  // Effect to handle body scroll during tutorial
  useEffect(() => {
    if (showTutorial) {
      document.body.style.overflow = 'hidden';
    } else {
      console.log('not showing tutorial');
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showTutorial]);

  const handleNextStep = () => {
    // Save current step's data before moving to next
    const currentStep = JOB_BOARD_TUTORIAL_STEPS[currentTutorialStep];
    const currentRef = nodeRefs[currentStep.id];
    if (currentRef?.current) {
      setPreviousStepData({
        step: currentStep,
        rect: currentRef.current.getBoundingClientRect(),
      });
    }
    setCurrentTutorialStep((prev) => prev + 1);
  };

  const handleTutorialComplete = () => {
    setShowTutorial(false);
    updateTutorialFlag('hasSeenJobBoardTutorial', true);
  };

  // Tutorial overlay component
  const TutorialOverlay = () => {
    if (!showTutorial) return null;

    const currentStep = JOB_BOARD_TUTORIAL_STEPS[currentTutorialStep];
    const currentRef = nodeRefs[currentStep.id];
    const rect = currentRef?.current?.getBoundingClientRect();

    if (!rect) return null;

    return (
      <>
        <div className="fixed inset-0 z-50">
          {rect && (
            <div
              className="absolute bg-transparent"
              style={{
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)',
                borderRadius: '0.5rem',
              }}
            />
          )}
        </div>

        <TutorialTooltip
          step={currentStep}
          steps={JOB_BOARD_TUTORIAL_STEPS}
          onNext={handleNextStep}
          onComplete={handleTutorialComplete}
          style={getTooltipStyle(currentStep, rect)}
        />
      </>
    );
  };

  // Update getTooltipStyle to accept step and rect as parameters
  const getTooltipStyle = (step, rect) => {
    if (!rect) return {};

    if (step.id === 'job-listings') {
      return {
        position: 'absolute',
        top: rect.top - 200,
        left: rect.left + rect.width / 2 - 150,
        width: '300px',
        zIndex: 51,
        '--arrow-orientation': 'down',
      };
    }

    return {
      position: 'absolute',
      top: rect.top + rect.height / 2 - 100,
      left: step.placement === 'left' ? rect.left - 320 : rect.right + 20,
      width: '300px',
      zIndex: 51,
      '--arrow-orientation': step.placement === 'left' ? 'right' : 'left',
    };
  };

  // Reset elementsReady when tutorial step changes
  useEffect(() => {
    if (showTutorial) {
      setElementsReady(false);
    }
  }, [currentTutorialStep]);

  // Debounce search to avoid too many API calls
  const handleSearch = (e) => {
    console.log('Search term changed:', e.target.value);
    setSearchTerm(e.target.value);
  };

  // Update useEffect dependencies to trigger fetch on searchTerm change
  useEffect(() => {
    console.log('Search/filters changed, resetting to page 1');
    console.log('Current search term:', searchTerm);
    console.log('Current filters:', advancedFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [searchTerm, advancedFilters, sortBy, sortOrder]);

  // Separate useEffect for fetching that depends on page, search, filters, and sorting
  useEffect(() => {
    console.log('Fetching roles with:', {
      page: pagination.page,
      searchTerm,
      advancedFilters,
      sortBy,
      sortOrder,
    });
    fetchRoles();
  }, [pagination.page, searchTerm, advancedFilters, sortBy, sortOrder]);

  const fetchRoles = async () => {
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: pagination.page,
        page_size: pagination.page_size,
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      // Add search term if present
      if (searchTerm) {
        console.log('Adding search term to params:', searchTerm);
        params.append('search_term', searchTerm);
      }

      // Add date filter if recent is selected
      if (filters.recent) {
        const fortyEightHoursAgo = new Date();
        fortyEightHoursAgo.setHours(fortyEightHoursAgo.getHours() - 48);
        params.append('min_date', fortyEightHoursAgo.toISOString());
      }

      // Add advanced filters - handle arrays by adding multiple parameters
      if (advancedFilters.jobFocus && advancedFilters.jobFocus.length > 0) {
        advancedFilters.jobFocus.forEach((focus) => {
          params.append('job_focus', focus);
        });
      }
      if (advancedFilters.visaStatus && advancedFilters.visaStatus.length > 0) {
        advancedFilters.visaStatus.forEach((status) => {
          params.append('visa_status', status);
        });
      }
      if (advancedFilters.workLocation && advancedFilters.workLocation.length > 0) {
        advancedFilters.workLocation.forEach((location) => {
          params.append('work_location', location);
        });
      }
      if (advancedFilters.internshipSeason && advancedFilters.internshipSeason.length > 0) {
        advancedFilters.internshipSeason.forEach((season) => {
          params.append('internship_season', season);
        });
      }
      if (advancedFilters.housingStatus && advancedFilters.housingStatus.length > 0) {
        advancedFilters.housingStatus.forEach((status) => {
          params.append('housing_status', status);
        });
      }
      if (advancedFilters.minEducationLevel) {
        params.append('min_education_level', advancedFilters.minEducationLevel);
      }
      if (advancedFilters.minGpa) {
        params.append('min_gpa', advancedFilters.minGpa);
      }
      if (advancedFilters.minGradYear) {
        params.append('min_grad_year', advancedFilters.minGradYear);
      }
      if (advancedFilters.maxGradYear) {
        params.append('max_grad_year', advancedFilters.maxGradYear);
      }
      if (advancedFilters.requiresSecurityClearance) {
        params.append('requires_security_clearance', 'true');
      }
      if (advancedFilters.companySize && advancedFilters.companySize.length > 0) {
        advancedFilters.companySize.forEach((size) => {
          params.append('company_size', size);
        });
      }

      const url = `/api/v1/role?${params.toString()}`;
      console.log('Fetching from URL:', url);

      const response = await axiosInstance.get(url);
      const data = response.data;

      console.log('Raw response data:', data);
      console.log('Roles from response:', data.roles);
      setRoles(data.roles);
      setPagination(data.pagination);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching roles:', err);

      // Check if it's an authentication error
      if (err.response?.status === 401) {
        console.log('Authentication failed - user may need to log in again');
        setError('Your session has expired. Please refresh the page or log in again.');
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to fetch roles');
      }
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSortChange = (value) => {
    if (value === sortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(value);
      setSortOrder('desc');
    }
  };

  const toggleFilter = (filterType) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: !prev[filterType],
    }));
  };

  const getFilteredRoles = () => {
    if (!Array.isArray(roles)) {
      console.log('Roles is not an array:', roles);
      return [];
    }

    // Filter out applied jobs if toggle is ON
    let filteredRoles = roles;
    if (showAppliedJobs) {
      filteredRoles = roles.filter((role) => {
        const hasApplied = currentApps?.some((app) => {
          const isMatchingJob =
            app.role_id === role.role_id ||
            (app.company_name === role.company_name && app.position_name === role.name);
          return isMatchingJob;
        });

        return !hasApplied; // Return true to keep jobs that are NOT in tracker
      });
    }

    // No need to sort for recommended jobs here since backend handles it with proper pagination
    return filteredRoles;
  };

  const handleRemoveFilter = (filterKey) => {
    setAdvancedFilters((prev) => ({
      ...prev,
      [filterKey]: filterKey === 'requiresSecurityClearance' ? false : '',
      // Clear both grad year fields if either is being removed
      ...(filterKey === 'minGradYear' || filterKey === 'maxGradYear'
        ? {
            minGradYear: '',
            maxGradYear: '',
          }
        : {}),
    }));
  };

  const getFilterLabel = (key, value) => {
    // Don't show any label if value is empty, null, undefined, or an empty string
    if (!value && value !== false) return '';

    switch (key) {
      case 'jobFocus':
        return `Job Focus: ${value}`;
      case 'visaStatus':
        return `Visa: ${value}`;
      case 'minEducationLevel':
        return `Education: ${value}`;
      case 'workLocation':
        return `Location: ${value}`;
      case 'internshipSeason':
        return `Season: ${value}`;
      case 'housingStatus':
        return `Housing: ${value}`;
      case 'minGpa':
        return `Min GPA: ${value}`;
      case 'minGradYear':
        return `Graduation Year: ${value}`;
      case 'maxGradYear':
        return ''; // Don't show a separate pill for maxGradYear
      case 'requiresSecurityClearance':
        return value ? 'Requires Security Clearance' : '';
      case 'companySize':
        return `Company Size: ${value}`;
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  // Show restriction message for Paul Allen School students
  if (isFromPaulAllenSchool || isFromUW) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="max-w-2xl mx-auto p-8">
          <div className="bg-white rounded-lg border border-gray-200 shadow-xs p-8 text-center">
            <img
              src="/badges/kitty_confuse.png"
              alt="Confused cat"
              className="w-24 h-24 mx-auto mb-4"
            />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Restricted</h2>
            <p className="text-gray-600 mb-6">
              The Paul Allen School of Computer Science & Engineering does not allow access to
              external job boards. This restriction is in place to avoid conflicts with the school's
              internal job board system.
            </p>
            <p className="text-gray-600 mb-6">
              Please use your department's official job board and career resources for internship
              and job opportunities.
            </p>
            <ExternalLink
              href="https://www.cs.washington.edu/academics/undergraduate/career-resources/"
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              Visit UW CSE Career Resources
              <svg className="ml-2 -mr-1 w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </ExternalLink>
            <div className="mt-6">
              <p className="text-sm text-gray-500">
                If you believe this is an error or have questions about this policy, please contact
                your academic advisor or the Paul Allen School career services.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const filteredRoles = getFilteredRoles();

  return (
    <div className="flex-1 px-3 flex flex-col items-center">
      <div className="px-4 sm:px-6 lg:px-8 lg:pb-7 mx-auto max-w-340 w-full">
        <TutorialOverlay />
        <TutorialRestartButton tutorialKey="hasSeenJobBoardTutorial" />

        {/* Main Container - matching Application Tracker layout */}
        <div className="max-w-full px-4 py-10 sm:px-6 lg:px-8 lg:py-14 mx-auto">
          <div className="-m-1.5 w-full">
            <div className="p-1.5 w-full inline-block align-middle">
              <div className="flex flex-col border border-gray-200 rounded-xl shadow-xs bg-white w-full">
                {/* Header - matching Application Tracker structure */}
                <div className="px-6 py-4 grid gap-3 md:flex md:justify-between md:items-center border-b border-gray-200">
                  {/* Search Input */}
                  <div className="sm:col-span-2 md:flex-1 md:max-w-sm lg:max-w-md">
                    <label htmlFor="job-search" className="sr-only">
                      Search
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="job-search"
                        name="job-search"
                        className="py-2 px-3 ps-11 block w-full border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                        placeholder="Search jobs by title, company, or description..."
                        value={searchTerm}
                        onChange={handleSearch}
                      />
                      <div className="absolute inset-y-0 inset-s-0 flex items-center pointer-events-none ps-4">
                        <svg
                          className="shrink-0 size-4 text-gray-400"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="11" cy="11" r="8" />
                          <path d="m21 21-4.3-4.3" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="sm:col-span-1 md:flex md:items-center md:justify-end">
                    <div className="flex justify-end gap-x-2">
                      {/* Toggle Switch */}
                      <div className="flex items-center">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={showAppliedJobs}
                            onChange={(e) => setShowAppliedJobs(e.target.checked)}
                          />
                          <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 peer-focus:ring-4 peer-focus:ring-blue-300 peer-focus:outline-hidden peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                          <span className="ml-3 text-sm font-medium text-gray-700">
                            Hide jobs I've applied to
                          </span>
                        </label>
                      </div>

                      {/* Filter Button - matching Application Tracker design */}
                      <div
                        className="hs-dropdown [--placement:bottom-right] relative inline-block"
                        data-hs-dropdown-auto-close="inside"
                      >
                        <button
                          id="job-filter-dropdown"
                          type="button"
                          className={`py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border shadow-xs hover:bg-gray-50 focus:outline-hidden disabled:opacity-50 disabled:pointer-events-none transition-colors ${
                            isFilterModalOpen
                              ? 'border-blue-200 bg-blue-50 text-blue-800 focus:bg-blue-50'
                              : 'border-gray-200 bg-white text-gray-800 focus:bg-gray-50'
                          }`}
                          onClick={() => setIsFilterModalOpen(true)}
                        >
                          <svg
                            className={`shrink-0 size-3.5 transition-transform duration-200 ${
                              isFilterModalOpen ? 'rotate-180' : ''
                            }`}
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M3 6h18" />
                            <path d="M7 12h10" />
                            <path d="M10 18h4" />
                          </svg>
                          Filter
                          {Object.values(advancedFilters).some((v) => v !== '' && v !== false) && (
                            <span className="ps-2 text-xs font-semibold text-blue-600 border-s border-gray-200">
                              {
                                Object.values(advancedFilters).filter(
                                  (v) => v !== '' && v !== false
                                ).length
                              }
                            </span>
                          )}
                        </button>
                      </div>

                      {/* Date Sort Control - matching Application Tracker button style */}
                      <button
                        onClick={() => handleSortChange('date')}
                        className="py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-xs hover:bg-gray-50 focus:outline-hidden focus:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
                      >
                        <svg
                          className="shrink-0 size-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        {sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
                        <svg
                          className={`shrink-0 size-3 transition-transform duration-200 ${
                            sortOrder === 'desc' ? 'rotate-180' : ''
                          }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
                {/* End Header */}

                {/* Active Filter Tags and Results Summary */}
                {(Object.values(advancedFilters).some((v) => v !== '' && v !== false) ||
                  pagination.total > 0) && (
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-600">{pagination.total} jobs found</span>
                      </div>
                      {Object.values(advancedFilters).some((v) => v !== '' && v !== false) && (
                        <button
                          onClick={() =>
                            setAdvancedFilters({
                              jobFocus: '',
                              visaStatus: '',
                              minEducationLevel: '',
                              workLocation: '',
                              internshipSeason: '',
                              housingStatus: '',
                              minGpa: '',
                              minGradYear: '',
                              maxGradYear: '',
                              requiresSecurityClearance: false,
                              companySize: '',
                            })
                          }
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Clear all filters
                        </button>
                      )}
                    </div>

                    {/* Active Filters Display */}
                    {Object.values(advancedFilters).some((v) => v !== '' && v !== false) && (
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(advancedFilters).map(([key, value]) => {
                          const label = getFilterLabel(key, value);
                          if (!label) return null;

                          return (
                            <span
                              key={key}
                              className="inline-flex items-center gap-x-1 py-1 px-2 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {label}
                              <button
                                type="button"
                                onClick={() => handleRemoveFilter(key)}
                                className="inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200"
                              >
                                <svg
                                  className="w-3 h-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Job Grid Container */}
                <div className="px-6 py-4">
                  {loading ? (
                    <div className="flex items-center justify-center min-h-[400px]">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                  ) : error ? (
                    <div className="flex items-center justify-center min-h-[400px]">
                      <div className="text-red-500">Error: {error}</div>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 gap-4" ref={nodeRefs['job-listings']}>
                        {console.log('Rendering roles:', roles)}
                        {roles.length === 0 ? (
                          <div className="text-center text-gray-500 py-12">
                            No roles found matching your criteria
                          </div>
                        ) : (
                          getFilteredRoles().map((role, index) => (
                            <div key={role.role_id}>
                              <JobComponent
                                role={role}
                                tutorialRefs={
                                  index === 0
                                    ? {
                                        addApplication: nodeRefs['add-application'],
                                        applyNow: nodeRefs['apply-now'],
                                      }
                                    : undefined
                                }
                              />
                            </div>
                          ))
                        )}
                      </div>

                      {/* Pagination Controls */}
                      {pagination.total_pages > 1 && (
                        <div className="flex justify-center items-center gap-x-2 mt-6">
                          <button
                            onClick={() =>
                              setPagination((prev) => ({
                                ...prev,
                                page: Math.max(1, prev.page - 1),
                              }))
                            }
                            disabled={pagination.page === 1}
                            className="py-2.5 px-3 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-xs hover:bg-gray-50 focus:outline-hidden focus:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
                          >
                            Previous
                          </button>
                          <span className="py-2.5 px-3 text-sm text-gray-600">
                            Page {pagination.page} of {pagination.total_pages}
                          </span>
                          <button
                            onClick={() =>
                              setPagination((prev) => ({
                                ...prev,
                                page: Math.min(prev.total_pages, prev.page + 1),
                              }))
                            }
                            disabled={pagination.page === pagination.total_pages}
                            className="py-2.5 px-3 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-xs hover:bg-gray-50 focus:outline-hidden focus:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Modal */}
      <JobFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={advancedFilters}
        onApplyFilters={setAdvancedFilters}
      />
    </div>
  );
}

export default JobBoard;
